import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {ChartConstants} from '@constants/pages/chart-constants';
import {
  PollingRateKey,
  StaticTextWidgetPollingRate
} from '@constants/pages/user-defined-page/static-text-widget-polling-rate';
import {UserDefinedWidgetType} from '@constants/pages/user-defined-page/user-defined-widget-type';
import {MetaData} from '@models/adminstration/meta-data';
import {Signal} from '@models/dashboard/signal';
import {Machine} from '@models/machine/machine';
import {ChartSignalEntry, Colors} from '@models/pages/user-defined-page/chart-signal-entry';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {TranslateService} from '@ngx-translate/core';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {ChartsConfigurationHelperService} from '@services/charts/helper/charts-configuration-helper.service';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {NotificationService} from '@services/notification/notification.service';
import {DragulaService} from 'ng2-dragula';
import {Subscription} from 'rxjs';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';

@Component({
  selector: 'scitis-text-widget-configuration',
  templateUrl: './text-widget-configuration.component.html',
  styleUrls: ['./text-widget-configuration.component.scss'],
  standalone: false
})
export class TextWidgetConfigurationComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();

  @Input()
  machineSignals: any;

  @Input()
  machine: Machine = new Machine();

  @Input()
  isTemplateWidget: boolean = false;

  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  hasDeletedSignalsChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  allUsableSignals: Signal[] = [];
  storedData: string[] = [];
  dragAndDropSubscription = new Subscription();
  dragAndDropItems = 'SIGNALS';
  dragAndDropStaticItems = 'STATIC_DATA';
  selectedSignalIds: number[] = [];
  isLoadingStaticData: boolean = true;
  selectedStaticData: string[] = [];
  pollingRate: StaticTextWidgetPollingRate[] = [];
  pollingRatePlaceholder: string = '';
  staticDataPlaceholder: string = '';
  selectedPollingRate: PollingRateKey | undefined;
  hasDeletedSignalsMap: Map<string, boolean> = new Map<string, boolean>();

  private machineProperties: string[] = ['category', 'createdBy', 'creationTime', 'location', 'machineTag'];

  constructor(private chartsConfigurationHelperService: ChartsConfigurationHelperService,
              private chartsHelperService: ChartsHelperService,
              private dragulaService: DragulaService,
              private sortingService: SortingService,
              private notification: NotificationService,
              private translate: TranslateService,
              private elasticMetaDataService: MetadataElasticSearchService) {
    this.chartsConfigurationHelperService.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignalsMap = hasDeletedSignals;
    });
    this.pollingRatePlaceholder = this.translate.instant('Select polling rate...');
    this.staticDataPlaceholder = this.translate.instant('Select static data...');
    this.useDragula(this.dragAndDropItems);
    this.useDragula(this.dragAndDropStaticItems);
  }

  get isTextWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.TEXT;
  }

  get isPollingRateValid() {
    return this.userDefinedWidget.userDefinedChartDetails.rangeValue >= 0;
  }

  get isStaticDataValid() {
    return this.selectedStaticData && this.selectedStaticData.length > 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.userDefinedChartDetails && this.allUsableSignals.length > 0) {
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
      this.emitConfigChanged();
    }
    if (this.allUsableSignals && (this.userDefinedWidget.userDefinedChartDetails.signals?.length ?? 0) > 0) {
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
      this.emitConfigChanged();
    }
  }

  ngOnInit(): void {
    this.userDefinedWidget.userDefinedChartDetails.signals ??= [];
    this.getStoredData();
    this.setPollingRates();
    this.setSelectedPollingRate();
    this.allUsableSignals = [...this.chartsConfigurationHelperService.filterMisconfiguredSignals(this.machineSignals)];
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
    this.emitConfigChanged();
  }

  @HostListener('document:keydown.escape') onKeydownHandler() {
    this.dragulaService.destroy(this.dragAndDropItems);
    this.dragulaService.destroy(this.dragAndDropStaticItems);
  }

  sortByLabel(signals: ChartSignalEntry[]): ChartSignalEntry[] {
    return this.sortingService.sortArrayByProperty(signals, 'label', true);
  }

  hasSignalBeenDeleted(signal: ChartSignalEntry): boolean {
    return !this.allUsableSignals.find(s => s.id == signal.signalId);
  }

  onSignalSelected($event: Signal | Signal[]) {
    this.userDefinedWidget.userDefinedChartDetails.signals = [];
    if (!$event) {
      this.emitConfigChanged();
      return;
    }

    const signals: Signal[] = Array.isArray($event) ? $event : [$event];

    for (const signal of signals) {

      if (this.shouldShowTooManySignalWarning()) {
        continue;
      }

      const chartSignal: ChartSignalEntry = new ChartSignalEntry(signal);
      chartSignal.colors = new Colors();
      chartSignal.colors.main = this.chartsConfigurationHelperService.convertHexColorToNumber(signal.signalColor);
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
      this.userDefinedWidget.userDefinedChartDetails.signals = [...this.userDefinedWidget.userDefinedChartDetails.signals, chartSignal];

    }
    this.userDefinedWidget.userDefinedChartDetails.signals = this.sortByLabel(this.userDefinedWidget.userDefinedChartDetails.signals);
    this.emitConfigChanged();
  }

  onAddStaticData($event: string | string[]) {
    this.userDefinedWidget.userDefinedChartDetails.staticData = [];
    if (!$event) {
      this.emitConfigChanged();
      return;
    }

    const staticData: string[] = Array.isArray($event) ? $event : [$event];
    for (const data of staticData) {
      if (this.shouldShowTooManySignalWarning()) {
        continue;
      }
      this.userDefinedWidget.userDefinedChartDetails.staticData = [...this.userDefinedWidget.userDefinedChartDetails.staticData, data];
    }
    this.emitConfigChanged();
  }

  onSetPollingRate($event: StaticTextWidgetPollingRate) {
    if (!$event) {
      this.userDefinedWidget.userDefinedChartDetails.rangeValue = -1;
      this.emitConfigChanged();
      return;
    }

    this.userDefinedWidget.userDefinedChartDetails.rangeValue = $event.pollingRateInS;
    this.emitConfigChanged();
  }

  onRemoveSignal(signal: ChartSignalEntry) {
    this.userDefinedWidget.userDefinedChartDetails.signals.splice(
        this.userDefinedWidget.userDefinedChartDetails.signals.findIndex(s => this.isTemplateWidget ? s.signalName.toLowerCase() === signal.signalName.toLowerCase() : s.signalId === signal.signalId),
        1);
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(String(this.userDefinedWidget.id), this.hasDeletedSignals()));
    this.emitConfigChanged();
  }

  onRemoveData(data: string) {
    this.userDefinedWidget.userDefinedChartDetails.staticData.splice(this.userDefinedWidget.userDefinedChartDetails.staticData.findIndex(a => a === data), 1);
    this.emitConfigChanged();
  }

  ngOnDestroy() {
    this.dragAndDropSubscription.unsubscribe();
  }

  getSignalUnit(signal: Signal | ChartSignalEntry) {
    return this.chartsHelperService.getSignalUnit(signal);
  }

  setIsUsingLiveDataAsSource() {
    this.userDefinedWidget.userDefinedChartDetails.staticData = [];
    this.userDefinedWidget.userDefinedChartDetails.usingStaticData = false;
    this.emitConfigChanged();
  }

  setIsUsingStoredDataAsSource() {
    this.userDefinedWidget.userDefinedChartDetails.signals = [];
    this.selectedSignalIds = [];
    this.userDefinedWidget.userDefinedChartDetails.usingStaticData = true;
    this.userDefinedWidget.userDefinedChartDetails.rangeValue = -1;
    this.emitConfigChanged();
  }

  private hasDeletedSignals() {
    return (this.userDefinedWidget.userDefinedChartDetails.signals ?? []).some(signal =>
        signal.signalId > 0 && !this.allUsableSignals.find(s => s.id == signal.signalId)
    );
  }

  private setSelectedPollingRate() {
    if (this.userDefinedWidget.userDefinedChartDetails.usingStaticData) {
      switch (this.userDefinedWidget.userDefinedChartDetails.rangeValue) {
        case 0:
          this.selectedPollingRate = PollingRateKey.NO_POLLING;
          break;
        case 14400:
          this.selectedPollingRate = PollingRateKey.FOUR_HOURS;
          break;
        case 28800:
          this.selectedPollingRate = PollingRateKey.EIGHT_HOURS;
          break;
        case 86400:
          this.selectedPollingRate = PollingRateKey.TWENTY_FOUR_HOURS;
          break;
      }
    }
  }

  private setPollingRate(pollingRateKey: PollingRateKey) {
    switch (pollingRateKey) {
      case PollingRateKey.FOUR_HOURS:
        const pollingRate4 = this.translate.instant('every 4 hours');
        return new StaticTextWidgetPollingRate(pollingRate4, 4 * 60 * 60, PollingRateKey.FOUR_HOURS);
      case PollingRateKey.EIGHT_HOURS:
        const pollingRate8 = this.translate.instant('every 8 hours');
        return new StaticTextWidgetPollingRate(pollingRate8, 8 * 60 * 60, PollingRateKey.EIGHT_HOURS);
      case PollingRateKey.TWENTY_FOUR_HOURS:
        const pollingRate24 = this.translate.instant('every 24 hours');
        return new StaticTextWidgetPollingRate(pollingRate24, 24 * 60 * 60, PollingRateKey.TWENTY_FOUR_HOURS);
      case PollingRateKey.NO_POLLING:
        const pollingRateNoPoll = this.translate.instant('upon refresh');
        return new StaticTextWidgetPollingRate(pollingRateNoPoll, 0, PollingRateKey.NO_POLLING);
    }
  }

  private setPollingRates() {
    const pollingRate4Hours = this.setPollingRate(PollingRateKey.FOUR_HOURS);
    const pollingRate8Hours = this.setPollingRate(PollingRateKey.EIGHT_HOURS);
    const pollingRate24Hours = this.setPollingRate(PollingRateKey.TWENTY_FOUR_HOURS);
    const pollingRateNoPolling = this.setPollingRate(PollingRateKey.NO_POLLING);

    this.pollingRate = [pollingRate4Hours, pollingRate8Hours, pollingRate24Hours, pollingRateNoPolling];
  }

  private getStoredData() {
    this.storedData = this.machineProperties;
    this.getMachineMetaData();
  }

  private getMachineMetaData() {
    if (!this.machine.id) {
      return;
    }
    this.elasticMetaDataService.listAllMachineMetaData(this.machine.id).subscribe({
      next: (elasticResponse: ElasticSearchResponseEntity) => {
        this.isLoadingStaticData = false;
        const machineMetaData = elasticResponse?.result?.data;
        if (machineMetaData) {
          this.filterMetaData(machineMetaData);
        }
      }, error: (error) => {
        this.isLoadingStaticData = false;
        console.log(error);
      }
    });
  }

  private filterMetaData(machineMetaData: MetaData[]) {
    for (const metaDataField of machineMetaData) {
      if (metaDataField.key) {
        this.storedData = [...this.storedData, metaDataField.key];
      }
    }

    if (this.userDefinedWidget.userDefinedChartDetails.staticData) {
      this.selectedStaticData = [...this.userDefinedWidget.userDefinedChartDetails.staticData];
    }
  }

  private shouldShowTooManySignalWarning(): boolean {
    if ((!this.userDefinedWidget.userDefinedChartDetails.usingStaticData && this.userDefinedWidget.userDefinedChartDetails.signals.length < ChartConstants.MAX_NUMBER_OF_SIGNAL_ON_TEXT_WIDGET) ||
        this.userDefinedWidget.userDefinedChartDetails.usingStaticData && this.userDefinedWidget.userDefinedChartDetails.staticData.length < ChartConstants.MAX_NUMBER_OF_SIGNAL_ON_TEXT_WIDGET) {
      return false;
    }

    this.notification.error(this.translate.instant('There are too many selected signals'), null, 'TextWidgetConfigurationComponent', true);
    return true;
  }

  private useDragula(dragulaName: string) {
    this.dragAndDropSubscription.add(this.dragulaService.drag(dragulaName)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
        })
    );
    this.dragAndDropSubscription.add(this.dragulaService.drop(dragulaName)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
        })
    );
    this.dragAndDropSubscription.add(this.dragulaService.over(dragulaName)
        .subscribe(({container}) => {
          container.classList.add('ex-over');
        })
    );
    this.dragAndDropSubscription.add(this.dragulaService.out(dragulaName)
        .subscribe(({container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private emitConfigChanged(): void {
    this.setSelectedSignals();
    this.hasDeletedSignalsChanged.emit(this.hasDeletedSignals());

    if (!this.userDefinedWidget.userDefinedChartDetails.usingStaticData) {
      this.emitLiveDataConfigChanged();
    } else {
      this.emitStaticDataConfigChanged();
    }
  }

  private setSelectedSignals() {
    if (this.userDefinedWidget.userDefinedChartDetails && this.userDefinedWidget.userDefinedChartDetails.signals) {
      const selectedSignals = this.userDefinedWidget.userDefinedChartDetails.signals;
      const selectedSignalIds: number[] = this.chartsConfigurationHelperService.getSelectedSignalIdsById(selectedSignals, this.allUsableSignals);
      this.selectedSignalIds = [...selectedSignalIds];
    }
  }

  private emitLiveDataConfigChanged() {
    if (!this.userDefinedWidget.userDefinedChartDetails.signals || this.userDefinedWidget.userDefinedChartDetails.signals.length === 0) {
      this.selectedSignalIds = [];
      this.isConfigurationValid.emit(false);
    } else {
      if (this.isTemplateWidget) {
        const selectedSignals = this.userDefinedWidget.userDefinedChartDetails.signals;
        const selectedSignalIds: number[] = this.chartsConfigurationHelperService.getSelectedSignalIds(selectedSignals, this.allUsableSignals);
        this.selectedSignalIds = [...selectedSignalIds];
      } else {
        this.selectedSignalIds = this.chartsConfigurationHelperService.getSelectedSignalIdsById(this.userDefinedWidget.userDefinedChartDetails.signals, this.allUsableSignals);
      }
      this.isConfigurationValid.emit(true);
    }
  }

  private emitStaticDataConfigChanged() {
    if (!this.userDefinedWidget.userDefinedChartDetails.staticData || this.userDefinedWidget.userDefinedChartDetails.staticData.length === 0) {
      this.selectedStaticData = [];
      this.isConfigurationValid.emit(false);
    } else if (this.userDefinedWidget.userDefinedChartDetails.rangeValue < 0) {
      this.isConfigurationValid.emit(false);
    } else {
      this.selectedStaticData = [...this.userDefinedWidget.userDefinedChartDetails.staticData];
      this.isConfigurationValid.emit(true);
    }
  }

}
